import useAuthButton from '@/hooks/useAuthButton';
import useFormHooks, { IAjaxJson } from '@/hooks/useForm';
import { Button, ButtonProps, Card, ConfigProvider, Drawer, DrawerProps, Form, Popconfirm, PopconfirmProps, Space, Spin, Table, Typography, type FormInstance, type FormProps } from 'antd';
import type { ColumnType, TableProps } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import { merge } from 'lodash';
import React, { memo, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { To, useNavigate } from 'react-router-dom';
import { AdvanceBreadcrumb } from '../Breadcrumb';
import { IAdvanceBreadcrumbProps } from '../Breadcrumb/AdvanceBreadcrumb';

declare module 'react' {
  function forwardRef<T, P = {}>(render: (props: P, ref: React.Ref<T>) => React.ReactElement | null): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

type PageParamsType = { page: number; pageSize: number; total: number };

export interface CommonLayoutInstance<T extends { params: object; model: object }> {
  /** 刷新 */
  reload: (p?: T['params'] & Partial<PageParamsType & { searchFromType: 'searchForm' }>) => void;
  loading: boolean;
  spinning: boolean;
  formRef: FormInstance<T['params']>;
  getAjaxByKey: (type: string, p?: any) => Promise<void>;
  resetDataSource?: () => void;
}

type OperateButtonType = {
  key?: string;
  ajaxKey: string;
  name: string;
  props?: ButtonProps;
  isToShowDrawer?: boolean;
  isToPopConfirm?: boolean;
  popconfirmProps?: PopconfirmProps;
  isToReload?: boolean;
  title?: string;
  sortData?: (data?: any) => any;
  isToAjax?: boolean;
  drawerProps?: DrawerProps;
  sortItem?: (data?: any, item?: OperateButtonType) => Partial<OperateButtonType>;
  filter?: (data?: any) => boolean;
  permission?: string;
  onClickBtn?: (data?: any) => void;
  isToPath?: boolean;
  sortPathParams?: (data?: any, item?: OperateButtonType) => To;
  onClick?: (data?: any, item?: OperateButtonType) => void;
};

export interface CommonDrawerInstance<T extends { params: object; model: object }> {
  open: boolean;
  show: (params: T['params'], settings: { title?: string; ajaxKey: string; isToReload?: boolean; isDetail?: boolean }) => void;
  hide: () => void;
  loading: boolean;
  spinning: boolean;
  formRef: FormInstance<T['params']>;
}

type CommonDrawerType = {
  ajaxJson?: IAjaxJson;
  formChildren?: React.ReactNode;
  formChildrenJson?: { [key: string]: React.ReactNode };
  renderFormChildren?: (props: { ajaxKey: string; isDetail: boolean }) => React.ReactNode;
  formProps?: FormProps;
  commonCbk?: (ajaxKey: string, values: any, responseValues?: any, params?: any) => void;
  props?: DrawerProps;
};

const { Text } = Typography;

function DrawerWithoutRef<T extends { params: object; model: object }>(
  { ajaxJson, formChildren, formProps, commonCbk, formChildrenJson, renderFormChildren, props }: CommonDrawerType,
  ref: React.ForwardedRef<CommonDrawerInstance<T>>,
) {
  const [open, setOpen] = useState<boolean>(false);
  const { form, spinning, loading, reload, onFinish } = useFormHooks<T['model'], T['params']>(
    merge(
      {
        get: {
          ajax: () => Promise.resolve([]),
        },
      },
      ajaxJson,
    ),
    {
      isNotToInitial: true,
      commonCbk: (...args) => {
        const [key] = args;
        commonCbk?.(...args);
        if (key !== 'get') {
          hide();
        }
      },
    },
  );

  const [ajaxKey, setAjaxKey] = useState<string>('');
  const [title, setTitle] = useState<string>('');

  const [isDetail, setIsDetail] = useState<boolean>(false);

  const show = (params: T['params'], settings: { title?: string; ajaxKey: string; isToReload?: boolean; isDetail?: boolean }) => {
    setOpen(true);
    setTitle(settings?.title || '');
    form?.resetFields();
    setAjaxKey(settings?.ajaxKey);
    setIsDetail(!!settings?.isDetail);
    if (settings?.isToReload) {
      reload(params);
    } else {
      // @ts-ignore
      form?.setFieldsValue(params);
    }
  };

  const hide = () => {
    setOpen(false);
  };

  useImperativeHandle(ref, () => {
    return {
      open,
      reload,
      formRef: form,
      loading,
      show,
      hide,
      spinning,
    };
  });
  return (
    <Drawer
      open={open}
      title={title}
      forceRender
      onClose={() => {
        hide();
      }}
      footer={
        isDetail ? (
          <></>
        ) : (
          <Space>
            <Button
              type="primary"
              onClick={async () => {
                const values = await form.validateFields();
                onFinish(values, ajaxKey);
              }}
              loading={loading}
            >
              确定
            </Button>
            <Button
              onClick={() => {
                hide();
              }}
            >
              取消
            </Button>
          </Space>
        )
      }
      {...props}
    >
      <Spin spinning={spinning}>
        <ConfigProvider theme={{ token: { colorTextDisabled: '#172c4d' } }}>
          <Form layout="vertical" {...formProps} form={form} onFinish={onFinish} name="drawerForm" requiredMark={!isDetail}>
            {formChildrenJson?.[ajaxKey] || formChildren || renderFormChildren?.({ isDetail, ajaxKey })}
          </Form>
        </ConfigProvider>
      </Spin>
    </Drawer>
  );
}

export const CommonDrawer = React.forwardRef(DrawerWithoutRef);

export const CommonButtonList = memo(
  ({ buttonList, onClickBtn, data, isTable }: { buttonList?: OperateButtonType[]; onClickBtn: (item: OperateButtonType, data?: any) => void; data?: any; isTable?: boolean }) => {
    const { checkPermissionDisabled } = useAuthButton();

    return (
      <Space {...(isTable ? { split: <Text type="secondary">/</Text> } : {})}>
        {buttonList
          ?.filter((item) => !item.filter?.(data))
          ?.map((baseItem) => {
            const item = { ...baseItem, ...(baseItem.sortItem?.(data, baseItem) || {}) };
            const disabled = checkPermissionDisabled(item.permission);
            const buttonElement = (
              <Button
                key={item.key || item.ajaxKey}
                {...item.props}
                {...(isTable ? { type: 'link' } : {})}
                disabled={disabled || item.props?.disabled}
                onClick={(e) => {
                  e.preventDefault();
                  if (!item.isToPopConfirm) {
                    onClickBtn(item, item.sortData?.(data) || data);
                    item.onClickBtn?.(item.sortData?.(data) || data);
                  }
                }}
                style={isTable ? { paddingLeft: 0, paddingRight: 0 } : {}}
              >
                {item.name}
              </Button>
            );
            return item.isToPopConfirm ? (
              <Popconfirm
                title=""
                okText="确定"
                cancelText="取消"
                placement="topRight"
                {...item.popconfirmProps}
                disabled={disabled || item.popconfirmProps?.disabled}
                key={item.key || item.ajaxKey}
                onConfirm={() => {
                  onClickBtn(item, item.sortData?.(data) || data);
                }}
              >
                {buttonElement}
              </Popconfirm>
            ) : (
              buttonElement
            );
          })}
      </Space>
    );
  },
);

function CommonLayoutWithoutRef<T extends { params: object; model: object; drawerParams: object; drawerModel: object }>(
  {
    breadcrumb,
    searchFormChildren,
    searchFormRightChildren,
    searchFormLeftChildren,
    columns,
    tableProps,
    searchFormProps,
    ajaxJson,
    operateButtonList,
    isHideOperateDetailButton,
    leftOperateButtonList,
    rightOperateButtonList,
    searchFormLeftOperateButtonList,
    searchFormRightOperateButtonList,
    drawerJson,
    commonCbk,
    hasRowSelection,
    isHideDrawer,
    mainType = 'table',
    mainFormChildren,
    queryPermission,
    isNotToInitial,
    mainBeforeContent,
    mainAfterContent,
    breadcrumbAfterContent,
    isHideMainForm = false,
    isHideMainTable = false,
    isHideTableDetailButton = false,
    isHideBreadcrumb,
    isHideCardCls,
    operationColumnProps,
    children,
    searchFormOkText = '查询',
    isHideSearchFormCancel = false,
    searchFormBeforeResetChildren,
    renderChildren,
  }: {
    breadcrumb: IAdvanceBreadcrumbProps;
    searchFormChildren?: React.ReactNode;
    searchFormRightChildren?: React.ReactNode;
    searchFormBeforeResetChildren?: React.ReactNode;
    searchFormLeftChildren?: React.ReactNode;
    searchFormProps?: Omit<FormProps, 'onFinish' | 'form' | 'layout'>;
    columns?: ColumnType<T['model']>[];
    tableProps?: Omit<TableProps<T['model']>, 'columns'>;
    ajaxJson: IAjaxJson;
    isHideOperateDetailButton?: boolean;
    operateButtonList?: OperateButtonType[];
    leftOperateButtonList?: OperateButtonType[];
    rightOperateButtonList?: OperateButtonType[];
    searchFormLeftOperateButtonList?: OperateButtonType[];
    searchFormRightOperateButtonList?: OperateButtonType[];
    searchFormOkText?: string;
    isHideSearchFormCancel?: boolean;
    drawerJson?: CommonDrawerType;
    commonCbk?: (ajaxKey: string, values: any, responseValues?: any, params?: any) => void;
    hasRowSelection?: boolean;
    isHideDrawer?: boolean;
    mainType?: 'table' | 'form' | 'self';
    mainFormChildren?: React.ReactNode;
    queryPermission?: string;
    isNotToInitial?: boolean;
    mainBeforeContent?: React.ReactNode;
    mainAfterContent?: React.ReactNode;
    breadcrumbAfterContent?: React.ReactNode;
    isHideMainForm?: boolean;
    isHideTableDetailButton?: boolean;
    isHideMainTable?: boolean;
    isHideBreadcrumb?: boolean;
    isHideCardCls?: boolean;
    operationColumnProps?: ColumnType<T['model']>;
    children?: ReactNode;
    renderChildren?: (data: { dataSource: T['model'][] }) => ReactNode;
  },
  ref: React.ForwardedRef<CommonLayoutInstance<T>>,
) {
  const [dataSource, setDataSource] = useState<T['model'][]>([]);

  const [pageParams, setPageParams] = useState<PageParamsType>({
    page: 1,
    pageSize: 20,
    total: 0,
  });

  const { form, spinning, loading, reload, getAjaxByKey, onFinish } = useFormHooks<T['model'], T['params']>(
    merge(
      {
        get:
          mainType === 'table'
            ? {
                ajax: () => Promise.resolve([]),
                isNotToSetValues: true,
                cbk: (values: ({ list: T['model'][] } & PageParamsType) | T['model'][]) => {
                  if (Array.isArray(values)) {
                    setDataSource(values || []);
                    return {};
                  }
                  const { list, ...rest } = values || {};
                  // @ts-ignore
                  setDataSource(list || []);
                  setPageParams(rest);
                  return {};
                },
              }
            : {
                ajax: () => Promise.resolve([]),
              },
      },
      ajaxJson,
    ),
    {
      commonCbk: (...args) => {
        const [key] = args;
        if (key === 'get') setSelectedRowKeys([]);
        commonCbk?.(...args);
      },
      isNotToInitial: !!isNotToInitial,
    },
  );

  const triggerToLoadData = (p?: Partial<PageParamsType & { searchFromType: 'searchForm' | 'pagination' }>) => {
    setPageParams((index) => ({ ...index, ...p }));
    reload(p);
  };

  const CommonDrawerRef = useRef<CommonDrawerInstance<{ params: T['drawerParams']; model: T['drawerModel'] }>>(null);
  useImperativeHandle(ref, () => {
    return {
      reload,
      formRef: form,
      loading,
      spinning,
      getAjaxByKey,
      resetDataSource: () => {
        setDataSource([]);
      },
    };
  });

  const navigate = useNavigate();

  const onClickBtn = (item: OperateButtonType, params?: any) => {
    // console.log(item, params);
    if (item.isToShowDrawer) {
      CommonDrawerRef.current?.show(params || {}, { ajaxKey: item.ajaxKey, title: item.name || item.title, isToReload: item.isToReload, isDetail: item.ajaxKey === 'detail' });
      return;
    }
    if (item.isToAjax) {
      // console.log('onClickBtn:', item.ajaxKey, params);
      getAjaxByKey(item.ajaxKey, params);
      return;
    }
    if (item.isToPath) {
      const to = item?.sortPathParams?.(params, item);
      to && navigate(to as To, { replace: true });
      item.onClick?.(params, item);
      return;
    }
    item.onClick?.(params, item);
  };

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const { checkPermissionDisabled } = useAuthButton();

  return (
    // eslint-disable-next-line no-template-curly-in-string
    <ConfigProvider form={{ validateMessages: { required: '${label}是必选字段' } }}>
      {!isHideDrawer && <CommonDrawer<{ params: T['drawerParams']; model: T['drawerModel'] }> {...drawerJson} ref={CommonDrawerRef}></CommonDrawer>}
      {!isHideBreadcrumb && <AdvanceBreadcrumb {...breadcrumb} background={require('@/assets/images/product-background.png')} />}
      {breadcrumbAfterContent}
      {mainType === 'table' && (
        <>
          <Card size="small" {...(isHideCardCls ? { styles: { body: { padding: '0' } }, style: { border: 0, background: 'rgba(255,255,255,0)' } } : {})} className={isHideCardCls ? '' : 'm20'}>
            <Form
              layout="inline"
              form={form}
              name="searchForm"
              onFinish={(values) => {
                triggerToLoadData({ ...values, page: 1, searchFromType: 'searchForm' });
              }}
              {...searchFormProps}
            >
              <Space wrap size={[0, 8]}>
                {searchFormLeftChildren}
                <CommonButtonList buttonList={searchFormLeftOperateButtonList} onClickBtn={onClickBtn}></CommonButtonList>
                {searchFormChildren}
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={spinning} disabled={checkPermissionDisabled(queryPermission)}>
                    {searchFormOkText}
                  </Button>
                </Form.Item>
                {searchFormBeforeResetChildren}
                {!isHideSearchFormCancel && (
                  <Form.Item>
                    <Button
                      type="default"
                      loading={spinning}
                      onClick={() => {
                        form.resetFields();
                        // triggerToLoadData({ page: 1, searchFromType: 'searchForm' });
                      }}
                    >
                      重置
                    </Button>
                  </Form.Item>
                )}
              </Space>
              {searchFormRightChildren}
              <CommonButtonList data={selectedRowKeys} buttonList={searchFormRightOperateButtonList} onClickBtn={onClickBtn}></CommonButtonList>
            </Form>
          </Card>
          {!isHideMainTable && (
            <Card size="small" {...(isHideCardCls ? { styles: { body: { padding: '0' } }, style: { border: 0, background: 'rgba(255,255,255,0)' } } : {})} className={isHideCardCls ? '' : 'm20'}>
              <Space direction="vertical" style={{ width: '100%' }} size={[16, 16]}>
                {mainBeforeContent}
                <Space className="justify-space-between">
                  <div>
                    <CommonButtonList buttonList={leftOperateButtonList} onClickBtn={onClickBtn}></CommonButtonList>
                  </div>
                  <div>
                    <CommonButtonList buttonList={rightOperateButtonList} onClickBtn={onClickBtn} data={selectedRowKeys}></CommonButtonList>
                  </div>
                </Space>
                <Table<T['model']>
                  loading={spinning}
                  dataSource={dataSource}
                  pagination={{
                    size: 'small',
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条`,
                    current: pageParams.page,
                    pageSize: pageParams.pageSize,
                    total: pageParams.total,
                    onChange: (page, pageSize) => {
                      triggerToLoadData({ page, pageSize, searchFromType: 'pagination' });
                    },
                  }}
                  bordered
                  rowKey="id"
                  size="small"
                  scroll={{ x: 'max-content' }}
                  columns={[
                    ...(columns || []),
                    ...(operateButtonList?.length
                      ? [
                          {
                            dataIndex: 'operate',
                            title: '操作',
                            render: (_v: any, record: T['model']) => {
                              return (
                                <CommonButtonList
                                  isTable
                                  buttonList={[
                                    ...(isHideTableDetailButton || isHideOperateDetailButton
                                      ? []
                                      : [
                                          {
                                            name: '查看',
                                            ajaxKey: 'detail',
                                            props: { type: 'primary' },
                                            isToShowDrawer: true,
                                            sortData: ({ id }) => ({ id }),
                                            isToReload: true,
                                          } as OperateButtonType,
                                        ]),
                                    ...operateButtonList,
                                  ]}
                                  onClickBtn={onClickBtn}
                                  data={record}
                                ></CommonButtonList>
                              );
                            },
                          },
                        ]
                      : []),
                  ]}
                  {...(hasRowSelection
                    ? {
                        rowSelection: {
                          selectedRowKeys,
                          onChange: (keys) => {
                            setSelectedRowKeys(keys);
                          },
                          ...(operationColumnProps as TableRowSelection<T['model']>),
                        },
                      }
                    : {})}
                  {...tableProps}
                />
                {mainAfterContent}
              </Space>
            </Card>
          )}
        </>
      )}
      {mainType === 'form' && !isHideMainForm && (
        <Card className="m20" size="small" loading={spinning}>
          {mainBeforeContent}
          <ConfigProvider theme={{ token: { colorTextDisabled: '#172c4d' } }}>
            <Form form={form} layout="vertical" name="mainForm" onFinish={onFinish}>
              {mainFormChildren}
            </Form>
          </ConfigProvider>
          {mainAfterContent}
        </Card>
      )}
      {!(mainType === 'form' && !isHideMainForm) && mainType !== 'table' && <Form form={form}></Form>}
      {children && <div className="m20">{children}</div>}
      {renderChildren && (
        <Card size="small" {...(isHideCardCls ? { styles: { body: { padding: '0' } }, style: { border: 0, background: 'rgba(255,255,255,0)' } } : {})} className={isHideCardCls ? '' : 'm20'}>
          {renderChildren({ dataSource })}
        </Card>
      )}
    </ConfigProvider>
  );
}

const CommonLayout = React.forwardRef(CommonLayoutWithoutRef);

export default CommonLayout;
